import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  UsersThree,
  CalendarBlank,
  MapTrifold,
  ClockClockwise,
  ArrowRight,
  Plus,
} from "@phosphor-icons/react";

const statCards = [
  { key: "workers", label: "People", icon: UsersThree, bg: "bg-emerald-200" },
  { key: "shifts", label: "Shift types", icon: ClockClockwise, bg: "bg-violet-200" },
  { key: "scheduled_today", label: "Scheduled today", icon: CalendarBlank, bg: "bg-orange-200" },
  { key: "routes_today", label: "Routes today", icon: MapTrifold, bg: "bg-yellow-200" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [workers, setWorkers] = useState([]);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/workers").then((r) => setWorkers(r.data)).catch(() => {});
    api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Dashboard</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
          Hello, {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="text-base font-medium text-slate-600 mt-1">
          Here is what is happening across your team today.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <div
            key={c.key}
            data-testid={`stat-${c.key}`}
            className={`${c.bg} border-2 border-black rounded-xl brutal-shadow p-5`}
          >
            <c.icon weight="bold" size={28} />
            <div className="font-display text-4xl font-black mt-3">{stats[c.key] ?? 0}</div>
            <div className="text-sm font-bold text-slate-700">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border-2 border-black rounded-xl brutal-shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold tracking-tight">Your people</h2>
            <Link
              to="/workers"
              data-testid="dash-manage-people"
              className="text-sm font-bold text-blue-600 flex items-center gap-1 hover:underline"
            >
              Manage <ArrowRight weight="bold" size={16} />
            </Link>
          </div>
          {workers.length === 0 ? (
            <div className="text-center py-10">
              <p className="font-medium text-slate-500 mb-4">No people added yet.</p>
              <Link
                to="/workers"
                className="inline-flex items-center gap-2 bg-blue-500 text-white border-2 border-black rounded-lg px-5 py-2.5 font-bold brutal-shadow-sm"
              >
                <Plus weight="bold" size={18} /> Add first person
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {workers.slice(0, 6).map((w) => (
                <div
                  key={w.id}
                  className="flex items-center gap-3 border-2 border-black rounded-lg p-3"
                >
                  <div
                    className="w-10 h-10 rounded-lg border-2 border-black flex items-center justify-center font-display font-black"
                    style={{ backgroundColor: w.color }}
                  >
                    {w.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold">{w.name}</div>
                    <div className="text-xs font-medium text-slate-500">
                      {w.hours_per_week || 0}h / week
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border-2 border-black rounded-xl brutal-shadow p-6">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Shift types</h2>
          <div className="space-y-3">
            {shifts.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between border-2 border-black rounded-lg px-3 py-2"
                style={{ backgroundColor: s.color }}
              >
                <span className="font-bold">{s.name}</span>
                <span className="font-mono text-sm font-bold">
                  {s.start_time}–{s.end_time}
                </span>
              </div>
            ))}
          </div>
          <Link
            to="/schedule"
            data-testid="dash-plan-shifts"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-violet-300 border-2 border-black rounded-lg px-5 py-2.5 font-bold brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
          >
            Plan the week <ArrowRight weight="bold" size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
